/**
 * @file app.ts
 * @description Root application component. Renders only the RouterOutlet —
 * all page content is provided by lazy-loaded route components. Also wires
 * SEO metadata updates (title, description, canonical) to each navigation
 * and owns the cookie-consent popup orchestration (the popup itself is a
 * presentational component in @kirigami/shared/ui).
 */
import {
  ChangeDetectionStrategy,
  Component,
  PLATFORM_ID,
  computed,
  effect,
  inject,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet, Data } from '@angular/router';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GoogleTagManagerService, SeoService } from '@kirigami/platform/browser';
import { CookieBannerComponent } from '@kirigami/shared/ui';
import { AnalyticsService } from './services/analytics.service';
import { normalizeRoutePath } from './services/route-path';
import { CookieConsentService } from './services/cookie-consent.service';
import { ToasterComponent } from './components/toaster/toaster.component';
import { KbIndexingPollingService } from './services/kb-indexing-polling.service';
import { ReportPollingService } from './services/report-polling.service';
import { PageTitleService } from './services/page-title.service';
import { dropRetiredStorage } from './services/retired-storage';
import { environment } from '../environments/environment';
import type { AppRouteData } from './route-data';
import { TermsAndConditionsComponent } from './components/shared/terms-and-conditions/terms-and-conditions.component';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, CookieBannerComponent, ToasterComponent, TermsAndConditionsComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly seoService = inject(SeoService);
  private readonly consentSvc = inject(CookieConsentService);
  private readonly gtm = inject(GoogleTagManagerService);
  private readonly analytics = inject(AnalyticsService);
  private readonly document = inject(DOCUMENT);

  /**
   * Route title spoken through the shell's persistent `role="status"` region
   * on every navigation after the first. SPA route changes are otherwise
   * silent to screen readers (WCAG 4.1.3).
   */
  private readonly pageTitle = inject(PageTitleService);
  readonly routeAnnouncement = this.pageTitle.announcement;

  /** True until the initial navigation has been handled (not announced). */
  private firstNavigation = true;

  /** Path of the last navigation, fragment excluded. See {@link isSamePageNavigation}. */
  private previousPath: string | null = null;

  /** `data.samePage` of the last navigation, or empty. See {@link isSamePageNavigation}. */
  private previousSamePage = '';

  /**
   * Eagerly instantiated so the polling service runs its boot
   * reconciliation pass on app start. The reference is intentionally
   * unused — its presence keeps the DI graph from tree-shaking the
   * service away.
   */

  private readonly _polling = inject(ReportPollingService);
  /** Eager for the same reason: it resumes stored References upload batches on boot. */
  private readonly _kbIndexing = inject(KbIndexingPollingService);

  /**
   * Whether the cookie popup is shown. Purely derived: visible until the
   * user records a choice (accept or decline), then never again.
   *
   * Never during prerendering. Consent lives in browser storage, which does
   * not exist on the server, so the service reports "no consent" for
   * everyone there. Baking the banner into the prerendered HTML made it
   * appear and then vanish on every load for visitors who had already
   * accepted. The server cannot know the answer, so it renders no answer.
   */
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly cookieBannerVisible = computed(() => this.isBrowser && !this.consentSvc.hasConsent());

  /** Whether the cookie-policy dialog is shown (popup link or footer Cookies link). */
  readonly cookiePolicyVisible = computed(() => this.consentSvc.policyOpen());

  constructor() {
    if (this.isBrowser) dropRetiredStorage();
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((event) => {
        const data = this.resolveDeepestRouteData();
        this.seoService.updateFromRouteData(data, this.router.url);
        const samePage = this.isSamePageNavigation(event.urlAfterRedirects, data);
        const announce = !samePage && !this.firstNavigation;
        if (!samePage) this.focusRoute();
        // A page that already knows its subject (a manuscript's title) replaces the route's title here.
        const seo = (data['seo'] ?? {}) as { title?: string };
        this.pageTitle.routeApplied(
          event.urlAfterRedirects.split(/[?#]/)[0],
          seo.title ?? 'Kirigami',
          announce,
        );
        // Buffered via AnalyticsService until statistics consent exists and
        // GTM has finished loading (see the effect below); the config tag has
        // send_page_view disabled, so this is the only source of page views.
        // Route *template* rather than the raw URL, so `/report/{uuid}` rows
        // aggregate into `/report/:id`. The exact URL is still available in
        // GA4 as the built-in `page_location`.
        this.analytics.track({
          event: 'page_view',
          page_path: normalizeRoutePath(this.router.routerState.snapshot.root),
        });
      });

    // Consent-gated GTM load. Only in production builds, and only once the
    // user has accepted the `statistics` cookie category. `addGtmToDom()` is
    // idempotent, so re-running the effect on consent changes is safe.
    effect(() => {
      const consent = this.consentSvc.consent();
      if (environment.production && consent?.choices.statistics) {
        void this.gtm.addGtmToDom();
      }
    });
  }

  onCookieAccept(): void {
    this.consentSvc.acceptAll();
    this.consentSvc.closePolicy();
  }

  onCookieDecline(): void {
    this.consentSvc.declineAll();
    this.consentSvc.closePolicy();
  }

  onCookiePolicyOpen(): void {
    this.consentSvc.openPolicy();
  }

  onCookiePolicyClose(): void {
    this.consentSvc.closePolicy();
  }

  /**
   * True when this navigation changed what the page already open shows and did
   * not open another one. Two kinds count: an anchor on the same path, such as
   * the landing's "Skip to footer" link, and a move between routes that share
   * a `data.samePage` name, which is how a page that writes a selection into
   * the URL opts in (the knowledge base list puts the chosen id in the path).
   * Every other navigation announces the page and moves focus, a change of
   * parameter or query string included.
   *
   * The router reports those as a NavigationEnd like any other, but no page
   * changed: announcing a title would tell a screen-reader user they had
   * navigated somewhere, and focusing `#main-content` would drag them back to
   * the top of the page they just asked to jump down, or out of the control
   * they were using.
   */
  private isSamePageNavigation(url: string, data: Data): boolean {
    const [path, fragment] = url.split('#');
    const named = (data as AppRouteData).samePage;
    const samePage = typeof named === 'string' ? named : '';
    const anchorJump = this.previousPath === path && !!fragment;
    const sameSelection = samePage !== '' && samePage === this.previousSamePage;
    this.previousPath = path;
    this.previousSamePage = samePage;
    return anchorJump || sameSelection;
  }

  /**
   * On navigation (after the first): move focus to the `#main-content`
   * landmark, so keyboard and screen-reader users land at the top of the new
   * page instead of on an unmounted link. The page's title is announced by
   * `PageTitleService`. Pages without the landmark just get the announcement.
   */
  private focusRoute(): void {
    if (this.firstNavigation) {
      this.firstNavigation = false;
      return;
    }
    queueMicrotask(() => {
      const main = this.document.getElementById('main-content');
      if (main) {
        main.setAttribute('tabindex', '-1');
        main.focus();
      }
    });
  }

  /** Walk the route tree and merge `data` from every level; deepest wins. */
  private resolveDeepestRouteData(): Data {
    let route: ActivatedRoute | null = this.activatedRoute;
    let data: Data = {};
    while (route) {
      if (route.snapshot.data && Object.keys(route.snapshot.data).length > 0) {
        data = { ...data, ...route.snapshot.data };
      }
      route = route.firstChild;
    }
    return data;
  }
}
